import type { ObjectClass, StatExtend } from '@/type';

import type { PieChartData } from '../type';

/**
 * 특정 클래스 ID에 대한 AI 카운트와 Human 카운트를 계산합니다.
 * baseClassId와 일치하거나, extendClass 중 하나와 일치하는 경우를 포함합니다.
 */
export const calculateClassCount = (
  statExtendClass: StatExtend[] | null | undefined,
  baseClassId: number,
  extendClasses?: ObjectClass[],
): { aiCount: number; humanCount: number } => {
  return (
    statExtendClass?.reduce(
      (acc, cur) => {
        const isMatch =
          cur.extendClassID === baseClassId ||
          extendClasses?.find(extend => extend.id === cur.extendClassID);

        if (isMatch) {
          return {
            aiCount: acc.aiCount + cur.aiCountValue,
            humanCount: acc.humanCount + cur.humanCountValue,
          };
        }
        return acc;
      },
      { aiCount: 0, humanCount: 0 },
    ) ?? { aiCount: 0, humanCount: 0 }
  );
};

/**
 * SuperClass 리스트를 기반으로 PieChartData를 생성합니다.
 */
export const calculateTotalClassData = (
  superClassList: ObjectClass[],
  statExtendClass: StatExtend[] | null | undefined,
): PieChartData[] => {
  return superClassList.map(item => {
    const { aiCount, humanCount } = calculateClassCount(
      statExtendClass,
      item.id,
      item.extendClass,
    );
    return {
      aiCount,
      humanCount,
      name: item.alias || item.name,
      id: item.id,
      color: item.color,
    };
  });
};

/**
 * 선택된 SuperClass의 extendClass들에 대한 PieChartData를 생성합니다.
 */
export const calculateExtendClassData = (
  extendClasses: ObjectClass[] | null | undefined,
  statExtendClass: StatExtend[] | null | undefined,
): PieChartData[] => {
  if (!extendClasses) return [];

  return extendClasses.map(item => {
    const { aiCount, humanCount } = calculateClassCount(
      statExtendClass,
      item.id,
    );
    return {
      aiCount,
      humanCount,
      name: item.alias || item.name,
      id: item.id,
      color: item.color,
    };
  });
};

/**
 * BaseClass와 ExtendClass를 합친 데이터를 생성합니다.
 * BaseClass의 카운트에서 ExtendClass들의 합을 뺀 값을 BaseClass로 설정합니다.
 */
export const calculateExtendClassDataWithBaseClass = (
  baseClassData: PieChartData,
  extendClassData: PieChartData[],
): PieChartData[] => {
  const totalExtendAiCount = extendClassData.reduce(
    (acc, cur) => acc + cur.aiCount,
    0,
  );
  const totalExtendHumanCount = extendClassData.reduce(
    (acc, cur) => acc + cur.humanCount,
    0,
  );

  return [
    {
      aiCount: baseClassData.aiCount - totalExtendAiCount,
      humanCount: baseClassData.humanCount - totalExtendHumanCount,
      name: baseClassData.name,
      id: baseClassData.id,
      color: baseClassData.color,
    },
    ...extendClassData,
  ];
};
